import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { companiesApi, directorsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Building2, Users, DollarSign, CheckCircle, ArrowRight, ArrowLeft, 
  Plus, Trash2, Loader2, UserPlus, X
} from 'lucide-react';
import { toast } from 'sonner';
import { companyInfo } from '../data/mock';
import { INDIAN_STATES_AND_UTS } from '../constants/indianStates';

const ENTITY_TYPES = [
  { value: 'private_limited', label: 'Private Limited Company' },
  { value: 'opc', label: 'One Person Company (OPC)' },
  { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
  { value: 'partnership', label: 'Partnership Firm' },
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'section_8', label: 'Section 8 Company (Non-Profit)' },
  { value: 'society_registered', label: 'Registered Society' },
  { value: 'trust', label: 'Trust' },
];

// Capital structure configuration based on entity type
// full_capital: Authorized capital, Paid-up capital, Face value, Number of shares
// contribution_only: Partner/Member contribution + Profit sharing %
// corpus_only: Only corpus amount (for trusts)
// none: No capital structure fields
const CAPITAL_STRUCTURE_CONFIG = {
  private_limited: 'full_capital',      // Full capital structure
  opc: 'full_capital',                  // Full capital structure
  section_8: 'full_capital',            // Section 8 still follows Companies Act
  llp: 'contribution_only',             // LLP Act - contribution + profit share
  partnership: 'contribution_only',     // Partnership Act - contribution + profit share
  trust: 'corpus_only',                 // Trust Act - only corpus
  society_registered: 'none',           // No capital structure
  proprietorship: 'none',               // No capital structure
};

const STATES = INDIAN_STATES_AND_UTS;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Company Info
  const [companyData, setCompanyData] = useState({
    company_name: '',
    entity_type: '',
    state: '',
    description: '',
    business_activity: '',
    has_existing_company: false,
  });

  // Step 2: Directors
  const [directors, setDirectors] = useState([
    { full_name: '', email: '', phone: '', pan_number: '', date_of_birth: '' }
  ]);

  // Step 3: Capital & Shareholders
  const [capitalData, setCapitalData] = useState({
    // Full capital structure (Pvt Ltd, OPC, Section 8)
    authorized_capital: '',
    paid_up_capital: '',
    share_face_value: '',
    // Contribution based (LLP, Partnership)
    total_contribution: '',
    // Trust corpus
    corpus_amount: '',
  });
  
  const [shareholders, setShareholders] = useState([]);
  
  // Partners state for LLP/Partnership
  const [partners, setPartners] = useState([
    { name: '', email: '', phone: '', date_of_birth: '', contribution: '', profit_share_percentage: '' }
  ]);

  // Authorized Signatory selection (Step 4 for multi-director entities)
  const [authorizedSignatory, setAuthorizedSignatory] = useState(null);

  // Get capital structure type based on entity
  const getCapitalStructureType = () => {
    return CAPITAL_STRUCTURE_CONFIG[companyData.entity_type] || 'full_capital';
  };

  // Check if authorized signatory selection is required
  const requiresAuthorizedSignatory = () => {
    const entityType = companyData.entity_type;
    // Single person entities don't need signatory selection
    if (['opc', 'proprietorship'].includes(entityType)) return false;
    // LLP/Partnership: required if multiple partners
    if (['llp', 'partnership'].includes(entityType)) return partners.length > 1;
    // Other entities: required if multiple directors
    return directors.length > 1;
  };

  // Get the list of people who can be authorized signatories
  const getSignatoryOptions = () => {
    if (['llp', 'partnership'].includes(companyData.entity_type)) {
      return partners.map((p, i) => ({ index: i, name: p.name, email: p.email, type: 'partner' }));
    }
    return directors.map((d, i) => ({ index: i, name: d.full_name, email: d.email, type: 'director' }));
  };

  const steps = [
    { id: 1, title: 'Business Info', icon: Building2 },
    { id: 2, title: companyData.entity_type === 'llp' || companyData.entity_type === 'partnership' ? 'Partners' : 'Directors', icon: Users },
    { id: 3, title: getCapitalStructureType() === 'none' ? 'Review' : 'Capital & Ownership', icon: DollarSign },
    { id: 4, title: 'Review & Submit', icon: CheckCircle },
  ];

  const addDirector = () => {
    setDirectors([...directors, { full_name: '', email: '', phone: '', pan_number: '', date_of_birth: '' }]);
  };

  const removeDirector = (index) => {
    if (directors.length > 1) {
      setDirectors(directors.filter((_, i) => i !== index));
    }
  };

  const updateDirector = (index, field, value) => {
    const updated = [...directors];
    updated[index][field] = value;
    setDirectors(updated);
  };

  const addShareholder = () => {
    setShareholders([...shareholders, { 
      shareholder_name: '', 
      shareholder_email: '', 
      shareholder_type: 'individual',
      number_of_shares: 0,
      linked_director_index: null 
    }]);
  };

  const removeShareholder = (index) => {
    setShareholders(shareholders.filter((_, i) => i !== index));
  };

  const updateShareholder = (index, field, value) => {
    const updated = [...shareholders];
    updated[index][field] = value;
    
    if (field === 'linked_director_index' && value !== null) {
      const director = directors[value];
      updated[index].shareholder_name = director.full_name;
      updated[index].shareholder_email = director.email;
    }
    
    setShareholders(updated);
  };

  // Partner management functions (for LLP/Partnership)
  const addPartner = () => {
    setPartners([...partners, { name: '', email: '', phone: '', date_of_birth: '', contribution: '', profit_share_percentage: '' }]);
  };

  const removePartner = (index) => {
    if (partners.length > 1) {
      setPartners(partners.filter((_, i) => i !== index));
      // Reset signatory if removed partner was selected
      if (authorizedSignatory === index) {
        setAuthorizedSignatory(null);
      } else if (authorizedSignatory > index) {
        setAuthorizedSignatory(authorizedSignatory - 1);
      }
    }
  };

  const updatePartner = (index, field, value) => {
    const updated = [...partners];
    updated[index][field] = value;
    setPartners(updated);
  };

  const getTotalProfitShare = () => {
    return partners.reduce((sum, p) => sum + (Number(p.profit_share_percentage) || 0), 0);
  };

  const getTotalPartnerContribution = () => {
    return partners.reduce((sum, p) => sum + (Number(p.contribution) || 0), 0);
  };

  // Check if all partners have valid contribution (> 0)
  const allPartnersHaveContribution = () => {
    return partners.every(p => Number(p.contribution) > 0);
  };

  // Check if all partners have valid profit share
  const allPartnersHaveProfitShare = () => {
    return partners.every(p => Number(p.profit_share_percentage) > 0);
  };

  const calculatePercentage = (shares) => {
    const totalShares = Number(capitalData.paid_up_capital) / Number(capitalData.share_face_value);
    return totalShares > 0 ? ((shares / totalShares) * 100).toFixed(2) : 0;
  };

  const getTotalAllocated = () => {
    return shareholders.reduce((sum, s) => sum + (Number(s.number_of_shares) || 0), 0);
  };

  const getTotalShareholderPercentage = () => {
    const totalShares = Number(capitalData.paid_up_capital) / Number(capitalData.share_face_value);
    if (totalShares <= 0) return 0;
    const allocated = getTotalAllocated();
    return ((allocated / totalShares) * 100).toFixed(2);
  };

  const validateStep = (step) => {
    const capitalType = getCapitalStructureType();
    const entityType = companyData.entity_type;
    
    switch (step) {
      case 1:
        if (!companyData.company_name) {
          toast.error('Company name is required');
          return false;
        }
        if (!companyData.entity_type) {
          toast.error('Entity type is required');
          return false;
        }
        if (!companyData.state) {
          toast.error('State of registration is required');
          return false;
        }
        return true;

      case 2:
        // LLP/Partnership: Validate partners
        if (entityType === 'llp' || entityType === 'partnership') {
          if (partners.length < 2) {
            toast.error(`${entityType === 'llp' ? 'LLP' : 'Partnership'} requires at least 2 partners`);
            return false;
          }
          for (let i = 0; i < partners.length; i++) {
            if (!partners[i].name) {
              toast.error(`Partner ${i + 1}: Name is required`);
              return false;
            }
            if (!partners[i].email) {
              toast.error(`Partner ${i + 1}: Email is required`);
              return false;
            }
          }
          return true;
        }
        
        // Other entities: Validate directors
        // OPC can have only 1 director
        if (entityType === 'opc' && directors.length > 1) {
          toast.error('OPC can have only one director');
          return false;
        }
        
        for (let i = 0; i < directors.length; i++) {
          if (!directors[i].full_name) {
            toast.error(`Director ${i + 1}: Name is required`);
            return false;
          }
          if (!directors[i].email) {
            toast.error(`Director ${i + 1}: Email is required`);
            return false;
          }
          if (!directors[i].phone) {
            toast.error(`Director ${i + 1}: Phone is required`);
            return false;
          }
        }
        return true;

      case 3:
        // Proprietorship & Society: No capital structure needed
        if (capitalType === 'none') {
          return true;
        }
        
        // Trust: Corpus amount is mandatory
        if (capitalType === 'corpus_only') {
          if (!capitalData.corpus_amount || Number(capitalData.corpus_amount) <= 0) {
            toast.error('Corpus amount is mandatory for Trust registration');
            return false;
          }
          return true;
        }
        
        // LLP/Partnership: Validate contribution and profit sharing
        if (capitalType === 'contribution_only') {
          // Check all partners have contribution > 0
          for (let i = 0; i < partners.length; i++) {
            if (!partners[i].contribution || Number(partners[i].contribution) <= 0) {
              toast.error(`Partner ${i + 1}: Capital contribution is mandatory and must be greater than 0`);
              return false;
            }
            if (!partners[i].profit_share_percentage || Number(partners[i].profit_share_percentage) <= 0) {
              toast.error(`Partner ${i + 1}: Profit sharing percentage is required`);
              return false;
            }
          }
          
          // Check total profit share = 100%
          const totalProfit = getTotalProfitShare();
          if (totalProfit !== 100) {
            toast.error(`Total profit sharing must equal 100%. Current total: ${totalProfit}%`);
            return false;
          }
          return true;
        }
        
        // Full capital structure (Pvt Ltd, OPC, Section 8)
        if (capitalType === 'full_capital') {
          // Validate mandatory capital fields
          if (!capitalData.authorized_capital || Number(capitalData.authorized_capital) <= 0) {
            toast.error('Authorized capital is required');
            return false;
          }
          if (!capitalData.paid_up_capital || Number(capitalData.paid_up_capital) <= 0) {
            toast.error('Paid-up capital is required');
            return false;
          }
          if (!capitalData.share_face_value || Number(capitalData.share_face_value) <= 0) {
            toast.error('Share face value is required');
            return false;
          }
          if (Number(capitalData.paid_up_capital) > Number(capitalData.authorized_capital)) {
            toast.error('Paid-up capital cannot exceed authorized capital');
            return false;
          }
          
          // Validate shareholders if any exist
          if (shareholders.length > 0) {
            const totalShares = Number(capitalData.paid_up_capital) / Number(capitalData.share_face_value);
            const allocated = getTotalAllocated();
            
            if (allocated !== totalShares) {
              toast.error(`Total shareholding must equal 100%. Allocated: ${allocated} shares, Required: ${totalShares} shares`);
              return false;
            }
          }
          return true;
        }
        return true;

      case 4:
        // Final validation before creation
        // Check authorized signatory if required
        if (requiresAuthorizedSignatory() && authorizedSignatory === null) {
          toast.error('Please select an authorized signatory for communication');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      const capitalType = getCapitalStructureType();
      // Skip Step 3 for entities with no capital structure (Society, Proprietorship)
      if (currentStep === 2 && capitalType === 'none') {
        setCurrentStep(4); // Jump to Review
      } else {
        setCurrentStep(Math.min(currentStep + 1, 4));
      }
    }
  };

  const prevStep = () => {
    const capitalType = getCapitalStructureType();
    // Handle going back from Review when Step 3 was skipped
    if (currentStep === 4 && capitalType === 'none') {
      setCurrentStep(2); // Jump back to Directors/Partners
    } else {
      setCurrentStep(Math.max(currentStep - 1, 1));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const capitalType = getCapitalStructureType();
    const isPartnershipEntity = companyData.entity_type === 'llp' || companyData.entity_type === 'partnership';
    
    try {
      const directorIds = [];
      
      // For LLP/Partnership, create partners as directors
      if (isPartnershipEntity) {
        for (const partner of partners) {
          const created = await directorsApi.create({
            full_name: partner.name,
            email: partner.email,
            phone: partner.phone || '',
            date_of_birth: partner.date_of_birth || null,
            country: 'IN'
          });
          directorIds.push(created.id);
        }
      } else {
        // For other entities, create directors
        for (const director of directors) {
          const created = await directorsApi.create({
            ...director,
            country: 'IN'
          });
          directorIds.push(created.id);
        }
      }

      // Build capital structure based on entity type
      let capitalStructure = {};
      if (capitalType === 'full_capital') {
        capitalStructure = {
          authorized_capital: capitalData.authorized_capital,
          paid_up_capital: capitalData.paid_up_capital,
          share_face_value: capitalData.share_face_value,
        };
      } else if (capitalType === 'contribution_only') {
        capitalStructure = {
          total_contribution: getTotalPartnerContribution(),
          partners: partners.map(p => ({
            name: p.name,
            contribution: p.contribution,
            profit_share_percentage: p.profit_share_percentage,
          })),
        };
      } else if (capitalType === 'corpus_only') {
        capitalStructure = {
          corpus_amount: capitalData.corpus_amount,
        };
      }
      // For 'none', capital_structure remains empty

      const company = await companiesApi.create({
        ...companyData,
        country: 'IN',
        capital_structure: capitalStructure,
        director_ids: directorIds,
      });

      // Add shareholders only for full capital structure entities
      if (capitalType === 'full_capital') {
        for (const shareholder of shareholders) {
          await companiesApi.addShareholder(company.id, {
            shareholder_name: shareholder.shareholder_name,
            shareholder_email: shareholder.shareholder_email || null,
            shareholder_type: shareholder.shareholder_type,
            number_of_shares: shareholder.number_of_shares,
            company_id: company.id,
            linked_director_id: shareholder.linked_director_index !== null 
              ? directorIds[shareholder.linked_director_index] 
              : null,
          });
        }
      }

      toast.success('Company created successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating company:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to create company';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || 'Validation error').join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={companyInfo.logo} alt={companyInfo.name} className="w-8 h-8" />
              <span className="text-xl font-semibold text-white">New Company Setup</span>
            </div>
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-slate-700">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                    isActive ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30' :
                    isCompleted ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-slate-800 text-slate-500 border border-slate-700'
                  }`}>
                    {isCompleted ? <CheckCircle className="w-6 h-6" /> : <StepIcon className="w-6 h-6" />}
                  </div>
                  <span className={`mt-2 text-sm font-medium ${
                    isActive ? 'text-emerald-400' : isCompleted ? 'text-emerald-400' : 'text-slate-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 rounded-full ${
                    currentStep > step.id ? 'bg-emerald-500' : 'bg-slate-700'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8">
          {/* Step 1: Business Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold text-white mb-1">Business Information</h2>
                <p className="text-slate-400">Tell us about your company</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Company Name *</label>
                  <Input
                    value={companyData.company_name}
                    onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                    placeholder="e.g., TechStart Solutions"
                    className="h-12 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Entity Type *</label>
                  <Select 
                    value={companyData.entity_type} 
                    onValueChange={(v) => setCompanyData({ ...companyData, entity_type: v })}
                  >
                    <SelectTrigger className="h-12 bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder="Select entity type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {ENTITY_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="text-white focus:bg-emerald-500 focus:text-white data-[highlighted]:bg-emerald-500 data-[highlighted]:text-white data-[state=checked]:bg-emerald-500 data-[state=checked]:text-white data-[highlighted]:font-medium">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">State of Registration *</label>
                  <Select 
                    value={companyData.state} 
                    onValueChange={(v) => setCompanyData({ ...companyData, state: v })}
                  >
                    <SelectTrigger className="h-12 bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {STATES.map((state) => (
                        <SelectItem key={state} value={state} className="text-white focus:bg-emerald-500 focus:text-white data-[highlighted]:bg-emerald-500 data-[highlighted]:text-white data-[state=checked]:bg-emerald-500 data-[state=checked]:text-white data-[highlighted]:font-medium">
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Nature of Business</label>
                  <Input
                    value={companyData.business_activity}
                    onChange={(e) => setCompanyData({ ...companyData, business_activity: e.target.value })}
                    placeholder="e.g., Software Development"
                    className="h-12 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Business Description</label>
                <Textarea
                  value={companyData.description}
                  onChange={(e) => setCompanyData({ ...companyData, description: e.target.value })}
                  placeholder="Brief description of your business activities..."
                  className="min-h-[100px] bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={companyData.has_existing_company}
                    onChange={(e) => setCompanyData({ ...companyData, has_existing_company: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500 bg-slate-800"
                  />
                  <span className="text-slate-300">I already have an existing company in India</span>
                </label>
                {companyData.has_existing_company && (
                  <p className="mt-3 text-sm text-emerald-400 ml-8">
                    Great! We'll help you with compliance and secretarial services.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Directors OR Partners (based on entity type) */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* LLP / Partnership - Show Partners */}
              {(companyData.entity_type === 'llp' || companyData.entity_type === 'partnership') ? (
                <>
                  <div>
                    <h2 className="text-2xl font-semibold text-white mb-1">
                      {companyData.entity_type === 'llp' ? 'Designated Partners' : 'Partners'}
                    </h2>
                    <p className="text-slate-400">
                      {companyData.entity_type === 'llp' 
                        ? 'Add all designated partners of the LLP' 
                        : 'Add all partners of the firm'}
                    </p>
                  </div>

                  {partners.map((partner, index) => (
                    <div key={index} className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                            <Users className="w-5 h-5 text-amber-400" />
                          </div>
                          <h3 className="font-medium text-white">Partner {index + 1}</h3>
                        </div>
                        {partners.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removePartner(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Partner Name *</label>
                          <Input
                            value={partner.name}
                            onChange={(e) => updatePartner(index, 'name', e.target.value)}
                            placeholder="Full name as per PAN"
                            className="h-11 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Email *</label>
                          <Input
                            type="email"
                            value={partner.email}
                            onChange={(e) => updatePartner(index, 'email', e.target.value)}
                            placeholder="partner@email.com"
                            className="h-11 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Phone</label>
                          <Input
                            type="tel"
                            value={partner.phone}
                            onChange={(e) => updatePartner(index, 'phone', e.target.value)}
                            placeholder="+91 98765 43210"
                            className="h-11 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Date of Birth</label>
                          <Input
                            type="date"
                            value={partner.date_of_birth}
                            onChange={(e) => updatePartner(index, 'date_of_birth', e.target.value)}
                            className="h-11 bg-slate-800 border-slate-600 text-white"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Capital Contribution (₹) *</label>
                          <Input
                            type="number"
                            value={partner.contribution}
                            onChange={(e) => updatePartner(index, 'contribution', e.target.value)}
                            placeholder="Enter contribution amount"
                            className="h-11 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                          />
                          <p className="text-xs text-slate-500 mt-1">Must be greater than 0</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Profit Sharing % *</label>
                          <div className="flex items-center gap-3">
                            <Input
                              type="number"
                              value={partner.profit_share_percentage}
                              onChange={(e) => updatePartner(index, 'profit_share_percentage', e.target.value)}
                              min="0"
                              max="100"
                              placeholder="0"
                              className="h-11 bg-slate-800 border-slate-600 text-white"
                            />
                            <span className="text-amber-400 font-medium">%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Show total profit share status */}
                  <div className={`p-4 rounded-lg border ${getTotalProfitShare() === 100 ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-amber-500/10 border-amber-500/20'}`}>
                    <p className={getTotalProfitShare() === 100 ? 'text-emerald-400' : 'text-amber-400'}>
                      Total Profit Share: <span className="font-bold text-white">{getTotalProfitShare()}%</span>
                      {getTotalProfitShare() !== 100 && <span className="ml-2">(Must equal 100%)</span>}
                    </p>
                  </div>

                  <Button 
                    variant="outline" 
                    onClick={addPartner} 
                    className="w-full h-14 border-dashed border-slate-600 text-slate-400 hover:text-amber-400 hover:border-amber-500 hover:bg-amber-500/10"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Add Another Partner
                  </Button>
                </>
              ) : (
                /* Other entities - Show Directors */
                <>
                  <div>
                    <h2 className="text-2xl font-semibold text-white mb-1">Directors / Founders</h2>
                    <p className="text-slate-400">Add all directors or founders of the company</p>
                  </div>

                  {directors.map((director, index) => (
                    <div key={index} className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                            <Users className="w-5 h-5 text-emerald-400" />
                          </div>
                          <h3 className="font-medium text-white">Director {index + 1}</h3>
                        </div>
                        {directors.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDirector(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Full Name *</label>
                          <Input
                            value={director.full_name}
                            onChange={(e) => updateDirector(index, 'full_name', e.target.value)}
                            placeholder="As per PAN card"
                            className="h-11 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Email *</label>
                          <Input
                            type="email"
                            value={director.email}
                            onChange={(e) => updateDirector(index, 'email', e.target.value)}
                            placeholder="director@email.com"
                            className="h-11 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Phone *</label>
                          <Input
                            type="tel"
                            value={director.phone}
                            onChange={(e) => updateDirector(index, 'phone', e.target.value)}
                            placeholder="+91 98765 43210"
                            className="h-11 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">Date of Birth</label>
                          <Input
                            type="date"
                            value={director.date_of_birth}
                            onChange={(e) => updateDirector(index, 'date_of_birth', e.target.value)}
                            className="h-11 bg-slate-800 border-slate-600 text-white"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-sm font-medium text-slate-400 mb-1.5 block">PAN Number</label>
                          <Input
                            value={director.pan_number}
                            onChange={(e) => updateDirector(index, 'pan_number', e.target.value.toUpperCase())}
                            placeholder="ABCDE1234F"
                            maxLength={10}
                            className="h-11 bg-slate-800 border-slate-600 text-white uppercase placeholder:text-slate-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button 
                    variant="outline" 
                    onClick={addDirector} 
                    className="w-full h-14 border-dashed border-slate-600 text-slate-400 hover:text-emerald-400 hover:border-emerald-500 hover:bg-emerald-500/10"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Add Another Director
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Step 3: Capital & Ownership (Conditional based on entity type) */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Full Capital Structure - Pvt Ltd, OPC, Section 8 */}
              {getCapitalStructureType() === 'full_capital' && (
                <>
                  <div>
                    <h2 className="text-2xl font-semibold text-white mb-1">Capital & Ownership</h2>
                    <p className="text-slate-400">Define your capital structure and shareholders</p>
                  </div>

                  {/* Capital Structure */}
                  <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-amber-400" />
                      </div>
                      <h3 className="font-medium text-white">Capital Structure</h3>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1.5 block">Authorized Capital (₹)</label>
                        <Input
                          type="number"
                          value={capitalData.authorized_capital}
                          onChange={(e) => setCapitalData({ ...capitalData, authorized_capital: Number(e.target.value) })}
                          className="h-11 bg-slate-800 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1.5 block">Paid-up Capital (₹)</label>
                        <Input
                          type="number"
                          value={capitalData.paid_up_capital}
                          onChange={(e) => setCapitalData({ ...capitalData, paid_up_capital: Number(e.target.value) })}
                          className="h-11 bg-slate-800 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-400 mb-1.5 block">Face Value per Share (₹)</label>
                        <Input
                          type="number"
                          value={capitalData.share_face_value}
                          onChange={(e) => setCapitalData({ ...capitalData, share_face_value: Number(e.target.value) })}
                          className="h-11 bg-slate-800 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                      <p className="text-emerald-400">
                        Total Shares: <span className="font-bold text-white">{(capitalData.paid_up_capital / capitalData.share_face_value).toLocaleString()}</span>
                      </p>
                    </div>
                  </div>

                  {/* Shareholders */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-white">Shareholders</h3>
                      <p className="text-sm text-slate-400">
                        Allocated: <span className="text-emerald-400 font-medium">{getTotalAllocated().toLocaleString()}</span> / {(capitalData.paid_up_capital / capitalData.share_face_value).toLocaleString()} shares
                      </p>
                    </div>

                    {shareholders.map((shareholder, index) => (
                      <div key={index} className="bg-slate-900 rounded-xl p-6 mb-4 border border-slate-700">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium text-slate-300">Shareholder {index + 1}</h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeShareholder(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-slate-400 mb-1.5 block">Link to Director</label>
                            <Select 
                              value={shareholder.linked_director_index?.toString() || 'manual'} 
                              onValueChange={(v) => updateShareholder(index, 'linked_director_index', v === 'manual' ? null : parseInt(v))}
                            >
                              <SelectTrigger className="h-11 bg-slate-800 border-slate-600 text-white">
                                <SelectValue placeholder="Select or enter manually" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="manual" className="text-slate-200">Enter manually</SelectItem>
                                {directors.map((d, i) => (
                                  <SelectItem key={i} value={i.toString()} className="text-slate-200">
                                    {d.full_name || `Director ${i + 1}`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-400 mb-1.5 block">Shareholder Type</label>
                            <Select 
                              value={shareholder.shareholder_type} 
                              onValueChange={(v) => updateShareholder(index, 'shareholder_type', v)}
                            >
                              <SelectTrigger className="h-11 bg-slate-800 border-slate-600 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="individual" className="text-slate-200">Individual</SelectItem>
                                <SelectItem value="body_corporate" className="text-slate-200">Body Corporate</SelectItem>
                                <SelectItem value="huf" className="text-slate-200">HUF</SelectItem>
                                <SelectItem value="trust" className="text-slate-200">Trust</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-400 mb-1.5 block">Name</label>
                            <Input
                              value={shareholder.shareholder_name}
                              onChange={(e) => updateShareholder(index, 'shareholder_name', e.target.value)}
                              placeholder="Shareholder name"
                              className="h-11 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                              disabled={shareholder.linked_director_index !== null}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-400 mb-1.5 block">Number of Shares</label>
                            <div className="flex items-center gap-3">
                              <Input
                                type="number"
                                value={shareholder.number_of_shares}
                                onChange={(e) => updateShareholder(index, 'number_of_shares', Number(e.target.value))}
                                className="h-11 bg-slate-800 border-slate-600 text-white"
                              />
                              <span className="text-emerald-400 font-medium whitespace-nowrap">
                                {calculatePercentage(shareholder.number_of_shares)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <Button 
                      variant="outline" 
                      onClick={addShareholder} 
                      className="w-full h-14 border-dashed border-slate-600 text-slate-400 hover:text-emerald-400 hover:border-emerald-500 hover:bg-emerald-500/10"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Add Shareholder
                    </Button>
                  </div>
                </>
              )}

              {/* Contribution Only - LLP, Partnership */}
              {getCapitalStructureType() === 'contribution_only' && (
                <>
                  <div>
                    <h2 className="text-2xl font-semibold text-white mb-1">Partner Contributions</h2>
                    <p className="text-slate-400">
                      {companyData.entity_type === 'llp' 
                        ? 'Define capital contribution and profit sharing as per LLP Agreement'
                        : 'Define capital contribution and profit sharing as per Partnership Deed'}
                    </p>
                  </div>

                  <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-amber-400" />
                      </div>
                      <h3 className="font-medium text-white">Contribution Summary</h3>
                    </div>
                    
                    <div className="space-y-4">
                      {partners.map((partner, index) => (
                        <div key={index} className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                          <div className="grid md:grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-slate-500 uppercase mb-1">Partner</p>
                              <p className="text-white font-medium">{partner.name || `Partner ${index + 1}`}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase mb-1">Contribution</p>
                              <p className="text-white font-medium">₹{(partner.contribution || 0).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase mb-1">Profit Share</p>
                              <p className="text-amber-400 font-medium">{partner.profit_share_percentage || 0}%</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <div className="flex justify-between">
                        <p className="text-amber-400">
                          Total Contribution: <span className="font-bold text-white">₹{getTotalPartnerContribution().toLocaleString()}</span>
                        </p>
                        <p className={getTotalProfitShare() === 100 ? 'text-emerald-400' : 'text-red-400'}>
                          Total Profit Share: <span className="font-bold text-white">{getTotalProfitShare()}%</span>
                          {getTotalProfitShare() !== 100 && <span className="ml-2 text-red-400">(Must equal 100%)</span>}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <p className="text-blue-400 text-sm">
                      💡 To edit partner contributions and profit sharing, go back to the Partners step.
                    </p>
                  </div>
                </>
              )}

              {/* Corpus Only - Trust */}
              {getCapitalStructureType() === 'corpus_only' && (
                <>
                  <div>
                    <h2 className="text-2xl font-semibold text-white mb-1">Trust Corpus</h2>
                    <p className="text-slate-400">Define the initial corpus amount for the Trust</p>
                  </div>

                  <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-amber-400" />
                      </div>
                      <h3 className="font-medium text-white">Corpus Amount</h3>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-400 mb-1.5 block">Initial Corpus (₹)</label>
                      <Input
                        type="number"
                        value={capitalData.corpus_amount}
                        onChange={(e) => setCapitalData({ ...capitalData, corpus_amount: Number(e.target.value) })}
                        placeholder="Enter corpus amount"
                        className="h-11 bg-slate-800 border-slate-600 text-white max-w-md"
                      />
                      <p className="text-slate-500 text-sm mt-2">
                        The corpus is the permanent fund of the Trust as per Indian Trusts Act, 1882.
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* No Capital Structure - Society, Proprietorship */}
              {getCapitalStructureType() === 'none' && (
                <div className="text-center py-12">
                  <p className="text-slate-400">
                    {companyData.entity_type === 'society_registered' 
                      ? 'Registered Societies do not have a capital structure. Membership subscriptions will be handled separately.'
                      : 'Proprietorships do not have a separate capital structure as they are not a distinct legal entity.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold text-white mb-1">Review & Submit</h2>
                <p className="text-slate-400">Please review all information before submitting</p>
              </div>

              {/* Company Summary */}
              <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="font-medium text-white">Business Details</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-800 rounded-lg">
                    <span className="text-xs text-slate-500 uppercase">Name</span>
                    <p className="font-medium text-white mt-1">{companyData.company_name}</p>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-lg">
                    <span className="text-xs text-slate-500 uppercase">Entity Type</span>
                    <p className="font-medium text-white mt-1">
                      {ENTITY_TYPES.find(t => t.value === companyData.entity_type)?.label}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-lg">
                    <span className="text-xs text-slate-500 uppercase">State</span>
                    <p className="font-medium text-white mt-1">{companyData.state}</p>
                  </div>
                  {/* Show capital info based on entity type */}
                  {getCapitalStructureType() === 'full_capital' && (
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <span className="text-xs text-slate-500 uppercase">Authorized Capital</span>
                      <p className="font-medium text-white mt-1">₹{capitalData.authorized_capital.toLocaleString()}</p>
                    </div>
                  )}
                  {getCapitalStructureType() === 'contribution_only' && (
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <span className="text-xs text-slate-500 uppercase">Total Contribution</span>
                      <p className="font-medium text-white mt-1">₹{getTotalPartnerContribution().toLocaleString()}</p>
                    </div>
                  )}
                  {getCapitalStructureType() === 'corpus_only' && (
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <span className="text-xs text-slate-500 uppercase">Corpus Amount</span>
                      <p className="font-medium text-white mt-1">₹{capitalData.corpus_amount.toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Directors/Partners Summary - Conditional */}
              {(companyData.entity_type === 'llp' || companyData.entity_type === 'partnership') ? (
                /* Partners Summary */
                <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-amber-400" />
                    </div>
                    <h3 className="font-medium text-white">Partners ({partners.length})</h3>
                  </div>
                  <div className="space-y-3">
                    {partners.map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-amber-400 text-sm font-medium">
                            {p.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <span className="font-medium text-white">{p.name}</span>
                            <p className="text-xs text-slate-500">{p.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-amber-400 font-medium">{p.profit_share_percentage}% profit</span>
                          <p className="text-xs text-slate-500">₹{(p.contribution || 0).toLocaleString()} contribution</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Directors Summary */
                <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                    <h3 className="font-medium text-white">Directors ({directors.length})</h3>
                  </div>
                  <div className="space-y-3">
                    {directors.map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-emerald-400 text-sm font-medium">
                            {d.full_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <span className="font-medium text-white">{d.full_name}</span>
                        </div>
                        <span className="text-slate-400 text-sm">{d.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shareholders Summary - Only for full capital structure */}
              {getCapitalStructureType() === 'full_capital' && shareholders.length > 0 && (
                <div className="bg-slate-900 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-amber-400" />
                    </div>
                    <h3 className="font-medium text-white">Shareholders ({shareholders.length})</h3>
                  </div>
                  <div className="space-y-3">
                    {shareholders.map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <span className="font-medium text-white">{s.shareholder_name}</span>
                        <span className="text-emerald-400 font-medium">
                          {s.number_of_shares.toLocaleString()} shares ({calculatePercentage(s.number_of_shares)}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Authorized Signatory Selection - Only for multiple directors/partners */}
              {requiresAuthorizedSignatory() && (
                <div className="bg-slate-900 rounded-xl p-6 border border-blue-500/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">Authorized Signatory *</h3>
                      <p className="text-xs text-slate-400">Select who will communicate on behalf of the entity</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {getSignatoryOptions().map((person) => (
                      <label 
                        key={person.index} 
                        className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-all ${
                          authorizedSignatory === person.index 
                            ? 'bg-blue-500/20 border border-blue-500/50' 
                            : 'bg-slate-800 border border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="radio"
                          name="authorizedSignatory"
                          checked={authorizedSignatory === person.index}
                          onChange={() => setAuthorizedSignatory(person.index)}
                          className="w-4 h-4 text-blue-500 border-slate-500 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-white">{person.name}</p>
                          <p className="text-xs text-slate-500">{person.email}</p>
                        </div>
                        <span className="text-xs text-slate-400 capitalize">{person.type}</span>
                      </label>
                    ))}
                  </div>
                  {authorizedSignatory === null && (
                    <p className="text-red-400 text-sm mt-3">⚠️ Please select an authorized signatory to proceed</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="h-12 px-6 border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-30"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            {currentStep < 4 ? (
              <Button onClick={nextStep} className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700 text-white">
                Next Step
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Create Company
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingPage;
OnboardingPage;
